"use client";

import { BlocksCompanyStage } from "../../../../__generated__/contentful-types";
import { useKickerContext } from "../../../../helpers/contentful/KickerContext";
import { getContentfulVideoUrl } from "../../../../helpers/contentful/getContentfulVideoUrl";
import { notNull } from "../../../../helpers/notNull";
import { Kicker } from "../../../kicker/kicker";
import { helpers } from "@contentful/rich-text-types";
import { CosmosIconChevronDown } from "@cosmos/web-react";
import classnames from "classnames/bind";
import { useState } from "react";
import styles from "./company-stage.module.css";

const cx = classnames.bind(styles);

export const CompanyStage = ({ block }: { block: BlocksCompanyStage }) => {
  const kickerKind = useKickerContext();

  const [activeItems, _setActiveItems] = useState<string[]>([]);

  const removeActiveItem = (itemId: string) =>
    _setActiveItems((current) => current.filter((item) => item !== itemId));

  const setActiveItem = (itemId: string) =>
    _setActiveItems((current) =>
      Array.from(
        // Using `Set` here as a mechanism to prevent duplicates
        new Set(current).add(itemId),
      ),
    );

  return (
    <div className={cx("container")}>
      <div className={cx("inner")}>
        <div className={cx("video-wrapper")}>
          <video
            className={cx("video")}
            preload="auto"
            autoPlay={true}
            playsInline={true}
            loop={true}
            muted={true}
            src={
              block.video?.url
                ? getContentfulVideoUrl(block.video.url)
                : undefined
            }
          />
        </div>
        <div className={cx("content")}>
          <div className={cx("intro")}>
            {block.kicker && (
              <h1 className={cx("kicker-wrapper")}>
                <Kicker
                  className={cx("kicker")}
                  kind={kickerKind}
                  text={block.kicker}
                />
              </h1>
            )}
            {/* If kicker exists, it should be the H1 */}
            {block.kicker ? (
              <p className={cx("title")}>{block.title}</p>
            ) : (
              <h1 className={cx("title")}>{block.title}</h1>
            )}
            {block.quote && <p className={cx("quote")}>{block.quote}</p>}
          </div>
          <div className={cx("main")}>
            <div className={cx("article")}>
              {block.article?.json.content.map((paragraph, index) => (
                <p
                  key={index}
                  className={cx("article-paragraph", {
                    "article-paragraph--bold":
                      paragraph.content.filter(helpers.isText)[0].marks[0]
                        ?.type === "bold",
                  })}
                >
                  {paragraph.content.filter(helpers.isText)[0].value}
                </p>
              ))}
            </div>
            <div className={cx("company-facts")}>
              <h3 className={cx("company-facts-title")}>
                {block.companyFactsTitle}
              </h3>
              {block.companyFactsCollection &&
                block.companyFactsCollection.items
                  .filter(notNull)
                  .map((companyFact, index) => (
                    <div
                      key={index}
                      className={cx("company-fact", {
                        "company-fact--active": activeItems.includes(
                          companyFact.sys.id,
                        ),
                      })}
                      onClick={() => {
                        if (activeItems.includes(companyFact.sys.id)) {
                          removeActiveItem(companyFact.sys.id);
                          return;
                        }
                        setActiveItem(companyFact.sys.id);
                      }}
                    >
                      <CosmosIconChevronDown
                        className={cx("company-fact-icon")}
                        aria-hidden="true"
                      />
                      <span className={cx("company-fact-title")}>
                        {companyFact.title}
                      </span>
                      <h2 className={cx("company-fact-fact")}>
                        {companyFact.fact}
                      </h2>
                      {companyFact.text?.json.content.map(
                        (paragraph, index2) => (
                          <p
                            key={index2}
                            className={cx("company-fact-text-paragraph")}
                          >
                            {paragraph.content.filter(helpers.isText)[0].value}
                          </p>
                        ),
                      )}
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
